// App.js — Response-Ability · A Jon Kabat-Zinn–inspired Life Skills Lab
// Shell only; imports gentle, practice-oriented tabs.

import React, { useEffect, useRef, useState } from "react";
import HomeTab from "./tabs/HomeTab"; // "Arriving" page
import PracticeMapTab from "./tabs/PracticeMapTab";
import WeeklyPracticeTab from "./tabs/WeeklyPracticeTab";
import SkillsLibraryTab from "./tabs/SkillsLibraryTab";
import ExperimentsTab from "./tabs/ExperimentsTab";
import SosTab from "./tabs/SosTab";
import AboutTab from "./tabs/AboutTab";
import AwarenessAttentionTab from "./tabs/AwarenessAttentionTab";
import MetaAwarenessTab from "./tabs/MetaAwarenessTab";

/* ==================== Styles (soft, contemplative) ==================== */
function CheerStyles() {
  const css = [
    ":root{--bg:#F8FAFC;--card:#FFFFFF;--ink:#020617;--ink-70:#020617B3;--muted:#6B7280;--line:#E2E8F0;--brand:#2563EB;--brand-600:#1D4ED8;--brand-10:#E0ECFF;--accent:#F97316;--accent-10:#FFF4E6;--focus:rgba(37,99,235,.35);--shadow:0 1px 2px rgba(15,23,42,.04),0 12px 24px rgba(15,23,42,.06)}",
    "*{box-sizing:border-box}",
    "button{font:inherit}",
    "body{margin:0;background:var(--bg);color:var(--ink);font:14px/1.5 Inter,system-ui,-apple-system,Segoe UI,Roboto}",
    ".container{max-width:72rem;margin:0 auto;padding:24px}",
    ".app-head{position:sticky;top:0;background:rgba(248,250,252,.94);backdrop-filter:saturate(115%) blur(6px);border-bottom:1px solid var(--line);z-index:10}",
    ".app-head .wrap{max-width:72rem;margin:0 auto;padding:12px 24px;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap}",
    ".brand{display:flex;align-items:center;gap:10px;font-weight:900;letter-spacing:.2px}",
    ".brand .logo{width:28px;height:28px;border-radius:999px;background:radial-gradient(circle at 0% 0%,var(--accent),var(--brand));box-shadow:inset 0 0 0 2px rgba(255,255,255,.8),0 4px 18px rgba(15,23,42,.3)}",
    ".section{margin:16px 0}",
    ".card{background:var(--card);border:1px solid var(--line);border-radius:16px;padding:16px;box-shadow:var(--shadow)}",
    ".card.lg{padding:24px;border-radius:20px}",
    ".section-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;gap:12px;flex-wrap:wrap}",
    ".h1{font-size:28px;line-height:34px;font-weight:800;margin:0}",
    ".h2{font-size:18px;line-height:1.3;font-weight:700;margin:0}",
    ".muted{color:var(--muted)}",
    ".sm{font-size:13px}",
    ".xs{font-size:12px}",
    ".chips{display:flex;flex-wrap:wrap;gap:8px}",
    ".pill{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border-radius:999px;background:var(--brand-10);color:var(--brand);border:1px solid #c6d4ff;font-size:13px;font-weight:700;box-shadow:var(--shadow)}",
    ".grid{display:grid;gap:12px}",
    ".md-g-3{grid-template-columns:repeat(auto-fit,minmax(240px,1fr))}",
    ".input, textarea.input{background:#fff;border:1px solid var(--line);border-radius:10px;padding:8px;min-height:36px;width:100%}",
    ".btn{background:var(--brand);border:1px solid var(--brand-600);color:#fff;border-radius:999px;padding:8px 12px;cursor:pointer;text-decoration:none;font-size:13px;transition:box-shadow .15s ease,transform .05s ease}",
    ".btn:hover{box-shadow:0 8px 22px rgba(37,99,235,.20)}",
    ".btn:active{transform:translateY(1px)}",
    ".btn.secondary{background:#fff;color:var(--ink);border-color:#c6d4ff}",
    ".btn.ghost{background:transparent;border-color:transparent;color:var(--brand)}",
    ".tabs{display:flex;gap:8px;flex-wrap:wrap}",
    ".tab{appearance:none;cursor:pointer;border-radius:999px;padding:8px 14px;border:1px solid #d0ddff;background:var(--brand-10);color:var(--brand);font-weight:700;box-shadow:var(--shadow);font-size:13px}",
    ".tab.active{background:#fff;color:#020617;border-color:var(--brand);box-shadow:0 2px 10px rgba(37,99,235,.18)}",
  ].join("\n");
  return <style>{css}</style>;
}

/* ============================== Overlay ================================== */
function Overlay({ open, onClose, title, children, size = "lg" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.activeElement;
    ref.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || "Dialog"}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background:
          "radial-gradient(80% 100% at 50% 0%, rgba(15,23,42,.75), rgba(15,23,42,.95))",
        backdropFilter: "saturate(115%) blur(6px)",
        zIndex: 50,
        overflow: "auto",
      }}
    >
      <div
        ref={ref}
        tabIndex={-1}
        style={{
          outline: "none",
          minHeight: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "28px 16px",
        }}
      >
        <div
          className="container"
          style={{
            background: "var(--card)",
            border: "1px solid var(--line)",
            borderRadius: 20,
            boxShadow: "0 20px 60px rgba(0,0,0,.4)",
            padding: 20,
            maxWidth: size === "sm" ? 560 : 1152,
          }}
        >
          <div className="section-head" style={{ marginBottom: 14 }}>
            <h2 className="h2" style={{ margin: 0 }}>
              {title || "Practice"}
            </h2>
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ============================== App Shell ================================ */

export default function App() {
  const [tab, setTab] = useState("overview");
  const [weeklyOpen, setWeeklyOpen] = useState(false);

  // Home tile router: everything gently funnels into practice areas
  const onOpenHomeCategory = (c) => {
    if (!c?.key) return;
    if (c.key === "storm") setTab("sos");
    else if (c.key === "awareness") setTab("awareness");
    else if (c.key === "emotions") setTab("skills");
    else if (c.key === "body") setTab("skills");
    else setTab("skills");
  };

  return (
    <>
      <CheerStyles />
      <div className="app-head">
        <div className="wrap">
          <div className="brand" aria-label="Response-Ability Life Skills Lab">
            <div className="logo" aria-hidden />
            <div>
              <div
                style={{
                  fontSize: 14,
                  color: "var(--ink-70)",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                Response-Ability
              </div>
              <div
                style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1 }}
              >
                A Mindfulness-Based Path
              </div>
            </div>
          </div>

          <div className="tabs" role="tablist" aria-label="Primary sections">
            {[
              ["overview", "Arriving"],
              ["awareness", "Awareness & Attention"],
              ["meta-awareness", "Meta-awareness"],
              ["map", "The Landscape of Practice"],
              ["week", "This Week’s Invitation"],
              ["skills", "Ways of Seeing & Being"],
              ["experiments", "Try It and See"],
              ["sos", "In the Storm"],
              ["about", "What Is This Path?"],
            ].map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                className={`tab ${tab === id ? "active" : ""}`}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ width: 120, textAlign: "right" }}>
            <a
              className="btn ghost"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.print();
              }}
            >
              Print
            </a>
          </div>
        </div>
      </div>

      <main className="container">
        {tab === "overview" && <HomeTab onOpenCategory={onOpenHomeCategory} />}

        {tab === "awareness" && (
          <AwarenessAttentionTab onOpenMeta={() => setTab("meta-awareness")} />
        )}

        {tab === "meta-awareness" && <MetaAwarenessTab />}

        {tab === "map" && (
          <PracticeMapTab onOpenWeek={() => setWeeklyOpen(true)} />
        )}

        {tab === "week" && <WeeklyPracticeTab />}
        {tab === "skills" && <SkillsLibraryTab />}
        {tab === "experiments" && <ExperimentsTab />}
        {tab === "sos" && <SosTab />}
        {tab === "about" && <AboutTab />}
      </main>

      <Overlay
        open={weeklyOpen}
        onClose={() => setWeeklyOpen(false)}
        title="This Week’s Invitation"
      >
        <WeeklyPracticeTab />
      </Overlay>
    </>
  );
}
